import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { TriggerNodeMetadata } from '../../explorer/models/nodeTypes';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { isBetaFeatureEnabled } from '../../../core/beta';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Trigger-specific context menu action types
 */
type TriggerContextMenuActionType =
  | 'copy-name'
  | 'script-trigger-as'
  | 'script-trigger-create'
  | 'script-trigger-drop'
  | 'script-trigger-enable'
  | 'script-trigger-disable';

const TriggerActions = {
  COPY_NAME: 'copy-name' as TriggerContextMenuActionType,
  SCRIPT_AS: 'script-trigger-as' as TriggerContextMenuActionType,
  SCRIPT_CREATE: 'script-trigger-create' as TriggerContextMenuActionType,
  SCRIPT_DROP: 'script-trigger-drop' as TriggerContextMenuActionType,
  SCRIPT_ENABLE: 'script-trigger-enable' as TriggerContextMenuActionType,
  SCRIPT_DISABLE: 'script-trigger-disable' as TriggerContextMenuActionType,
};

/**
 * Generate context menu actions for a trigger node.
 * Enable/Disable only shown for MSSQL and PostgreSQL.
 */
export function generateTriggerContextMenuActions(
  isEnabled: boolean,
  engine?: string,
): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [
    { id: TriggerActions.COPY_NAME, label: 'Copy trigger name', icon: 'clipboard' },
    {
      id: TriggerActions.SCRIPT_AS,
      label: 'Script as',
      icon: 'code',
      submenu: [
        { id: TriggerActions.SCRIPT_CREATE, label: 'CREATE', icon: 'file-plus' },
        { id: TriggerActions.SCRIPT_DROP, label: 'DROP', icon: 'trash' },
      ],
    },
  ];

  // Enable/Disable only for MSSQL and PostgreSQL
  if (engine === 'mssql' || engine === 'pgsql') {
    if (isEnabled) {
      actions.push({ id: TriggerActions.SCRIPT_DISABLE, label: 'Disable', icon: 'power-off' });
    } else {
      actions.push({ id: TriggerActions.SCRIPT_ENABLE, label: 'Enable', icon: 'play' });
    }
  }

  // Open in Editor and Compile for Debug for Oracle triggers
  if (engine === 'oracle') {
    actions.push({ id: 'open-in-editor', label: 'Open', icon: 'code' });
    if (isBetaFeatureEnabled('pl-sql-debugger')) {
      actions.push({ id: 'compile-for-debug', label: 'Compile for Debug', icon: 'bug' });
    }
  }

  return actions;
}

/**
 * Generate DROP TRIGGER DDL (engine-aware)
 */
function generateTriggerDropDdl(
  metadata: TriggerNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  if (engine === 'pgsql') {
    // PostgreSQL: DROP TRIGGER name ON schema.table
    return `DROP TRIGGER ${quoteId(metadata.triggerName)} ON ${qualifyTable(metadata.schema, metadata.tableName)};\n`;
  }
  if (engine === 'sqlite') {
    return `DROP TRIGGER ${quoteId(metadata.triggerName)};\n`;
  }
  // MSSQL, MySQL: DROP TRIGGER schema.triggerName
  return `DROP TRIGGER ${qualifyTable(metadata.schema, metadata.triggerName)};\n`;
}

/**
 * Generate ENABLE/DISABLE TRIGGER DDL (MSSQL/PostgreSQL only)
 */
function generateTriggerToggleDdl(
  metadata: TriggerNodeMetadata,
  engine: string,
  enable: boolean,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const verb = enable ? 'ENABLE' : 'DISABLE';
  if (engine === 'mssql') {
    return `${verb} TRIGGER ${quoteId(metadata.triggerName)} ON ${qualifyTable(metadata.schema, metadata.tableName)};\n`;
  }
  // PostgreSQL
  return `ALTER TABLE ${qualifyTable(metadata.schema, metadata.tableName)} ${verb} TRIGGER ${quoteId(metadata.triggerName)};\n`;
}

/**
 * Generate a CREATE TRIGGER template when definition cannot be fetched
 */
function generateTriggerCreateTemplate(
  metadata: TriggerNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);
  const timing = metadata.timing || 'AFTER';
  const events = metadata.events || 'INSERT';

  if (engine === 'pgsql') {
    const funcName = `${metadata.triggerName}_func`;
    return [
      `-- Trigger function (retrieve with: SELECT pg_get_functiondef(p.oid) FROM pg_proc p JOIN pg_trigger t ON t.tgfoid = p.oid JOIN pg_class c ON t.tgrelid = c.oid JOIN pg_namespace n ON c.relnamespace = n.oid WHERE t.tgname = '${metadata.triggerName}' AND n.nspname = '${metadata.schema}')`,
      `CREATE OR REPLACE FUNCTION ${qualifyTable(metadata.schema, funcName)}()`,
      `    RETURNS trigger`,
      `    LANGUAGE plpgsql`,
      `AS $function$`,
      `BEGIN`,
      `    -- trigger function body`,
      `    RETURN NEW;`,
      `END;`,
      `$function$;`,
      ``,
      `CREATE TRIGGER ${quoteId(metadata.triggerName)}`,
      `    ${timing} ${events} ON ${fullTable}`,
      `    FOR EACH ROW`,
      `    EXECUTE FUNCTION ${qualifyTable(metadata.schema, funcName)}();`,
      ``,
    ].join('\n');
  }
  if (engine === 'mysql') {
    const singleEvent = events.split(',')[0].trim() || 'INSERT';
    return `CREATE TRIGGER ${quoteId(metadata.triggerName)}\n    ${timing} ${singleEvent} ON ${fullTable}\n    FOR EACH ROW\nBEGIN\n    -- trigger body\nEND;\n`;
  }
  if (engine === 'sqlite') {
    const singleEvent = events.split(',')[0].trim() || 'INSERT';
    return `CREATE TRIGGER ${quoteId(metadata.triggerName)}\n    ${timing} ${singleEvent} ON ${quoteId(metadata.tableName)}\nBEGIN\n    -- trigger body\nEND;\n`;
  }
  // MSSQL
  return `CREATE TRIGGER ${qualifyTable(metadata.schema, metadata.triggerName)}\n    ON ${fullTable}\n    ${timing} ${events}\nAS\nBEGIN\n    -- trigger body\nEND;\n`;
}

/**
 * Try to fetch the trigger definition from the database
 */
async function fetchTriggerDefinition(
  metadata: TriggerNodeMetadata,
  engine: string,
): Promise<string | null> {
  const { executeQueryV2 } = await import('./queryExecutor');
  const { createExplorerExecutionId } = await import('./explorerQueryUtils');

  const esc = (s: string) => s.replace(/'/g, "''");
  let sql: string;

  if (engine === 'mssql') {
    sql = `SELECT definition FROM sys.sql_modules WHERE object_id = OBJECT_ID(N'${esc(metadata.schema)}.${esc(metadata.triggerName)}')`;
  } else if (engine === 'pgsql') {
    sql = `SELECT pg_get_triggerdef(t.oid, true) AS definition FROM pg_trigger t JOIN pg_class c ON t.tgrelid = c.oid JOIN pg_namespace n ON c.relnamespace = n.oid WHERE t.tgname = '${esc(metadata.triggerName)}' AND n.nspname = '${esc(metadata.schema)}' AND c.relname = '${esc(metadata.tableName)}'`;
  } else if (engine === 'sqlite') {
    sql = `SELECT sql AS definition FROM sqlite_master WHERE type='trigger' AND name='${esc(metadata.triggerName)}'`;
  } else {
    return null; // MySQL: fall back to template
  }

  try {
    const result = await executeQueryV2({
      connectionId: metadata.connectionId,
      sql,
      database: metadata.database,
      executionId: createExplorerExecutionId(metadata.connectionId),
    });
    const definition = result.resultSets?.[0]?.rows?.[0]?.definition;
    return typeof definition === 'string' && definition.trim() ? definition.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Fetch the PostgreSQL trigger function definition via pg_get_functiondef()
 */
async function fetchPgTriggerFunctionDef(
  metadata: TriggerNodeMetadata,
): Promise<string | null> {
  const { executeQueryV2 } = await import('./queryExecutor');
  const { createExplorerExecutionId } = await import('./explorerQueryUtils');

  const esc = (s: string) => s.replace(/'/g, "''");
  const sql = `SELECT pg_get_functiondef(t.tgfoid) AS funcdef FROM pg_trigger t JOIN pg_class c ON t.tgrelid = c.oid JOIN pg_namespace n ON c.relnamespace = n.oid WHERE t.tgname = '${esc(metadata.triggerName)}' AND n.nspname = '${esc(metadata.schema)}' AND c.relname = '${esc(metadata.tableName)}'`;

  try {
    const result = await executeQueryV2({
      connectionId: metadata.connectionId,
      sql,
      database: metadata.database,
      executionId: createExplorerExecutionId(metadata.connectionId),
    });
    const funcdef = result.resultSets?.[0]?.rows?.[0]?.funcdef;
    return typeof funcdef === 'string' && funcdef.trim() ? funcdef.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Execute a trigger context menu action
 */
export function executeTriggerContextMenuAction(
  actionId: string,
  metadata: TriggerNodeMetadata,
): void {
  if (actionId === TriggerActions.COPY_NAME) {
    void writeClipboardText(metadata.triggerName);
    return;
  }

  if (actionId === 'open-in-editor') {
    void (async () => {
      const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');
      const { createDefaultPlSqlEditorContent } = await import('../../workspace/plsql-editor/types');
      const { findExistingEditorTab } = await import('../../workspace/pl-sql-debugger/hooks/debugHelpers');

      const { addTab, tabs, setActiveTab } = useWorkspaceStore.getState();
      const existingId = findExistingEditorTab(tabs, metadata.schema, metadata.triggerName, metadata.connectionId);
      if (existingId) { setActiveTab(existingId); return; }

      const conn = getConnectionById(metadata.connectionId);
      addTab({
        title: `${metadata.schema}.${metadata.triggerName}`,
        type: 'plsql-editor',
        content: createDefaultPlSqlEditorContent(
          metadata.triggerName,
          metadata.schema,
          metadata.database,
          'trigger',
        ),
        connectionInfo: {
          connectionId: metadata.connectionId,
          connectionName: safeConnectionAlias(conn.connection, 'Unnamed Connection'),
          serverHost: safeConnectionServer(conn.activeConnection?.connection),
          database: metadata.database,
        },
      });
    })();
    return;
  }

  if (actionId === 'compile-for-debug') {
    void (async () => {
      try {
        const result = await window.electronAPI!.oracleDebugger.compileForDebug({
          connectionId: metadata.connectionId,
          schema: metadata.schema,
          objectName: metadata.triggerName,
          objectType: 'trigger',
        });
        if (result.success) {
          toast.success({ title: 'Compiled with debug info' });
        } else {
          toast.error({ title: 'Compilation failed', description: result.error ?? 'Unknown error' });
        }
      } catch (error) {
        toast.error({ title: 'Error', description: error instanceof Error ? error.message : 'Compilation failed' });
      }
    })();
    return;
  }

  if (
    actionId === TriggerActions.SCRIPT_CREATE ||
    actionId === TriggerActions.SCRIPT_DROP ||
    actionId === TriggerActions.SCRIPT_ENABLE ||
    actionId === TriggerActions.SCRIPT_DISABLE
  ) {
    void handleTriggerScript(metadata, actionId);
  }
}

/**
 * Generate trigger script and open in a new query tab
 */
async function handleTriggerScript(
  metadata: TriggerNodeMetadata,
  actionId: string,
): Promise<void> {
  const { getScriptProvider } = await import('../../scripting');
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');

  const { activeConnection, connection, engine } = getConnectionById(metadata.connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const provider = getScriptProvider(engine);
  const quoteId = (name: string) => provider.quoteIdentifier(name);
  const qualifyTable = (schema: string, table: string) => provider.qualifyTableName(schema, table);

  let script: string;
  let titleVerb: string;

  if (actionId === TriggerActions.SCRIPT_CREATE) {
    const fetched = await fetchTriggerDefinition(metadata, engine);
    if (fetched && engine === 'pgsql') {
      // For PostgreSQL, also fetch the trigger function body
      const funcDef = await fetchPgTriggerFunctionDef(metadata);
      const triggerDef = `${fetched}${fetched.endsWith(';') ? '' : ';'}`;
      script = funcDef
        ? `${funcDef}${funcDef.endsWith(';') ? '' : ';'}\n\n${triggerDef}\n`
        : `-- Trigger function could not be retrieved. Run:\n-- SELECT pg_get_functiondef(t.tgfoid) FROM pg_trigger t JOIN pg_class c ON t.tgrelid = c.oid JOIN pg_namespace n ON c.relnamespace = n.oid WHERE t.tgname = '${metadata.triggerName}' AND n.nspname = '${metadata.schema}';\n\n${triggerDef}\n`;
    } else {
      script = fetched
        ? `${fetched}${fetched.endsWith(';') ? '' : ';'}\n`
        : generateTriggerCreateTemplate(metadata, engine, quoteId, qualifyTable);
    }
    titleVerb = 'CREATE';
  } else if (actionId === TriggerActions.SCRIPT_DROP) {
    script = generateTriggerDropDdl(metadata, engine, quoteId, qualifyTable);
    titleVerb = 'DROP';
  } else if (actionId === TriggerActions.SCRIPT_ENABLE) {
    script = generateTriggerToggleDdl(metadata, engine, true, quoteId, qualifyTable);
    titleVerb = 'ENABLE';
  } else {
    script = generateTriggerToggleDdl(metadata, engine, false, quoteId, qualifyTable);
    titleVerb = 'DISABLE';
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${metadata.triggerName}_${titleVerb}-${queryNumber}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId: metadata.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: metadata.database,
    },
  });
}
